import React, { useState } from "react";
import {
  Container,
  Typography,
  Grid,
  Paper,
  Box,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
} from "@mui/material";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import Layout from "../components/layout/LegalLayout";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const PerformanceReporting = () => {
  const [reportType, setReportType] = useState("monthly");

  // Sample Performance Data
  const revenueData = {
    labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
    datasets: [
      {
        label: "Revenue",
        data: [65000, 59000, 80000, 81000, 56000, 95000],
        borderColor: "rgb(75, 192, 192)",
        tension: 0.1,
      },
      {
        label: "Projected Revenue",
        data: [67000, 62000, 82000, 83000, 58000, 97000],
        borderColor: "rgb(255, 99, 132)",
        borderDash: [5, 5],
      },
    ],
  };

  const expenseData = {
    labels: ["Operating", "Marketing", "Tech", "Overhead", "Legal"],
    datasets: [
      {
        data: [45000, 22000, 15000, 10000, 8000],
        backgroundColor: [
          "rgba(255, 99, 132, 0.8)",
          "rgba(54, 162, 235, 0.8)",
          "rgba(255, 206, 86, 0.8)",
          "rgba(75, 192, 192, 0.8)",
          "rgba(153, 102, 255, 0.8)",
        ],
      },
    ],
  };

  const profitabilityData = {
    labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
    datasets: [
      {
        label: "Net Profit Margin",
        data: [22, 25, 30, 28, 23, 32],
        borderColor: "rgb(54, 162, 235)",
        backgroundColor: "rgba(54, 162, 235, 0.2)",
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
      },
    },
  };

  return (
    <Layout>
      <Container maxWidth="xl">
        <Typography variant="h4" gutterBottom>
          Performance Reporting
        </Typography>

        <Box sx={{ mb: 3 }}>
          <FormControl variant="outlined" sx={{ minWidth: 200 }}>
            <InputLabel>Report Type</InputLabel>
            <Select
              value={reportType}
              onChange={(e) => setReportType(e.target.value)}
              label="Report Type"
            >
              <MenuItem value="monthly">Monthly Report</MenuItem>
              <MenuItem value="quarterly">Quarterly Report</MenuItem>
              <MenuItem value="annual">Annual Report</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Grid container spacing={3}>
          {/* Revenue Tracking */}
          <Grid item xs={12} md={8}>
            <Paper elevation={3} sx={{ p: 2 }}>
              <Typography variant="h6">Revenue Tracking</Typography>
              <Line
                data={revenueData}
                options={{
                  ...chartOptions,
                  plugins: {
                    ...chartOptions.plugins,
                    title: {
                      ...chartOptions.plugins.title,
                      text: "Revenue Trend",
                    },
                  },
                }}
              />
            </Paper>
          </Grid>

          {/* Key Performance Indicators */}
          <Grid item xs={12} md={4}>
            <Paper elevation={3} sx={{ p: 2, height: "100%" }}>
              <Typography variant="h6">KPI Summary</Typography>
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}
              >
                <Card>
                  <CardContent>
                    <Typography variant="subtitle1">Total Revenue</Typography>
                    <Typography variant="h6" color="primary">
                      $435,000
                    </Typography>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle1">
                      Net Profit Margin
                    </Typography>
                    <Typography variant="h6" color="success.main">
                      27.5%
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            </Paper>
          </Grid>

          {/* Expense Breakdown */}
          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ p: 2 }}>
              <Typography variant="h6">Expense Distribution</Typography>
              <Doughnut
                data={expenseData}
                options={{
                  ...chartOptions,
                  plugins: {
                    ...chartOptions.plugins,
                    title: {
                      ...chartOptions.plugins.title,
                      text: "Expense Categories",
                    },
                  },
                }}
              />
            </Paper>
          </Grid>

          {/* Profitability Trend */}
          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ p: 2 }}>
              <Typography variant="h6">Profitability Trend</Typography>
              <Line
                data={profitabilityData}
                options={{
                  ...chartOptions,
                  plugins: {
                    ...chartOptions.plugins,
                    title: {
                      ...chartOptions.plugins.title,
                      text: "Net Profit Margin %",
                    },
                  },
                }}
              />
            </Paper>
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, display: "flex", justifyContent: "center", gap: 2 }}>
          <Button variant="contained" color="primary">
            Download Report
          </Button>
          <Button variant="outlined" color="secondary">
            Share Report
          </Button>
        </Box>
      </Container>
    </Layout>
  );
};

export default PerformanceReporting;
